import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { sectorDetailData } from '../../../data/sectorData';

const getPlayerSignalStyle = (signal: string): string => {
  const styles: Record<string, string> = {
    'LEADER': 'bg-data-green/20 text-data-green',
    'STRONG': 'bg-data-blue/20 text-data-blue',
    'GROWING': 'bg-data-amber/20 text-data-amber',
    'EMERGING': 'bg-gray-600/20 text-gray-400',
  };
  return styles[signal] || 'bg-gray-600/20 text-gray-400';
};

const getPotentialStyle = (potential: string): string => {
  return potential === 'VERY HIGH' ? 'text-data-green' : 'text-data-amber';
};

interface EcommercePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const EcommercePanel: React.FC<EcommercePanelProps> = ({ isMinimized, onMinimize }) => {
  const { ecommerce } = sectorDetailData;
  const { market, subsectors, keyPlayers, metrics, gaps } = ecommerce;

  return (
    <Panel title="E-COMMERCE" subtitle={`HOLD • Score ${ecommerce.score}`} status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full gap-2 overflow-auto custom-scrollbar">
        {/* Header Stats */}
        <div className="grid grid-cols-3 gap-2 p-2 bg-terminal-bg rounded">
          <div className="text-center">
            <div className="text-xs text-gray-500">TAM 2024 → 2028</div>
            <div className="text-sm font-bold">
              <span className="text-white">${market.tam2024}B</span>
              <span className="text-gray-500 mx-1">→</span>
              <span className="text-data-amber">${market.tam2028}B</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">CAGR</div>
            <div className="text-sm font-bold text-data-amber">{market.cagr}%</div>
            <div className="text-xs text-data-red bg-data-red/20 px-1.5 rounded mt-0.5">HIGH COMP</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">PENETRATION</div>
            <div className="text-sm font-bold text-white">{market.penetration}%</div>
            <div className="w-full h-1 bg-terminal-border rounded mt-1">
              <div className="h-full bg-data-amber rounded" style={{ width: `${(market.penetration / market.targetPenetration) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* Subsectors */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Subsectors (by CAGR)</div>
          <div className="space-y-1.5">
            {subsectors.map((sub) => (
              <div key={sub.name} className="flex items-center text-xs gap-2">
                <span className="text-gray-300 w-28 truncate">{sub.name}</span>
                <span className="text-white w-12 font-mono">${sub.size}B</span>
                <span className="text-data-green w-12 font-mono">+{sub.cagr}%</span>
                <span className="text-gray-500 w-16 truncate">{sub.leader}</span>
                <span className={`flex-1 text-right ${sub.gap === 'WIDE OPEN' ? 'text-data-green font-medium' : 'text-gray-400'}`}>
                  {sub.gap === 'WIDE OPEN' ? '★ ' : ''}{sub.gap}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Key Players & Market Metrics */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-terminal-bg rounded p-2">
            <div className="text-xs text-gray-500 uppercase mb-1.5">Key Players</div>
            {keyPlayers.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs py-1">
                <span className="text-white font-medium">{p.name}</span>
                <span className="text-gray-400 font-mono">${p.valuation}M</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${getPlayerSignalStyle(p.signal)}`}>
                  {p.signal}
                </span>
              </div>
            ))}
          </div>
          <div className="bg-terminal-bg rounded p-2">
            <div className="text-xs text-gray-500 uppercase mb-1.5">Market Metrics</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-400">Online Shoppers</span>
                <span className="text-white">{metrics.onlineShoppers}M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Avg Order</span>
                <span className="text-white">${metrics.avgOrderValue}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Mobile Share</span>
                <span className="text-white">{metrics.mobileShare}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Returns Rate</span>
                <span className="text-data-amber">{metrics.returnsRate}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Opportunities */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Top Opportunities</div>
          {gaps.map((g) => (
            <div key={g.opportunity} className="flex items-center text-xs py-1">
              <span className="text-white flex-1">{g.opportunity}</span>
              <span className={`w-16 ${getPotentialStyle(g.potential)}`}>[{g.potential}]</span>
              <span className="text-gray-400 w-16">{g.investment}</span>
              <span className="text-gray-500 w-14 text-right">{g.timeToMarket}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default EcommercePanel;
